<script lang="ts">
  import { flashcardActions, selectedTargetLanguage, filteredCategories, flashcards } from '../../stores/flashcards';
  import { Plus, X, Loader2, Check, Sparkles, Save } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import Card from '../ui/card.svelte';
  import { resolveApiUrl } from '../../lib/utils';
  import { userSettings } from '../../stores/settings';

  let {
    open = false,
    mode = 'add', // 'add' or 'edit'
    cardId = '',
    onClose = () => {},
  }: {
    open?: boolean;
    mode?: 'add' | 'edit';
    cardId?: string;
    onClose?: () => void;
  } = $props();

  // Map language codes to flag emojis
  const languageFlags: Record<string, string> = {
    'cs': '🇨🇿',
    'ar': '🇸🇦',
    'en': '🇬🇧',
    'de': '🇩🇪',
    'fr': '🇫🇷',
    'es': '🇪🇸',
    'ja': '🇯🇵',
    'zh': '🇨🇳',
    'ru': '🇷🇺',
    'it': '🇮🇹',
    'pt': '🇵🇹',
    'ko': '🇰🇷',
    'tr': '🇹🇷',
    'pl': '🇵🇱',
    'nl': '🇳🇱',
    'sv': '🇸🇪',
    'da': '🇩🇰',
    'no': '🇳🇴',
    'fi': '🇫🇮',
    'el': '🇬🇷',
    'he': '🇮🇱',
    'hi': '🇮🇳',
    'th': '🇹🇭',
    'vi': '🇻🇳',
    'id': '🇮🇩',
    'ms': '🇲🇾',
    'uk': '🇺🇦',
  };

  function getLanguageFlag(code: string): string {
    const normalizedCode = code.toLowerCase().split('-')[0];
    return languageFlags[normalizedCode] || '🌐';
  }

  // For edit mode - find the card being edited
  let card = $derived(mode === 'edit' && cardId ? $flashcards.find(c => c.id === cardId) : undefined);
  let isLoading = $derived(open && mode === 'edit' && cardId && !card);

  // Get primary language flag
  const primaryLanguageFlag = $derived(
    getLanguageFlag($userSettings?.preferred_language || 'cs')
  );

  // Get target language flag
  const targetLanguageFlag = $derived.by(() => {
    if (mode === 'edit' && card) {
      return getLanguageFlag(card.targetLanguage.code);
    } else if (mode === 'add' && $selectedTargetLanguage) {
      return getLanguageFlag($selectedTargetLanguage.code);
    }
    return '🌐';
  });

  // Check if target language is RTL (from database)
  const isTargetRTL = $derived.by(() => {
    if (mode === 'edit' && card) {
      return card.targetLanguage.is_rtl ?? false;
    } else if (mode === 'add' && $selectedTargetLanguage) {
      return $selectedTargetLanguage.is_rtl ?? false;
    }
    return false;
  });

  let formData = $state({
    primary: '',
    target: '',
    phonetic: '',
    transliteration: '',
    categoryId: '',
    isLearned: false,
  });

  let errorMessage = $state('');
  let isSaving = $state(false);
  let isLoadingTranslations = $state(false);
  let translationSuggestions = $state<string[]>([]);
  let translationSource = $state<'deepl' | 'google' | 'openai' | 'none'>('none');
  const translationSourceLabel = $derived.by(() => {
    switch (translationSource) {
      case 'deepl':
        return 'DeepL';
      case 'google':
        return 'Google Translate';
      case 'openai':
        return 'OpenAI';
      default:
        return 'AI asistenta';
    }
  });
  let showSuggestions = $state(false);
  let selectedSuggestions = $state<Set<string>>(new Set());
  let showManualInput = $state(false);
  let isDebouncing = $state(false);
  let debounceTimer: number | null = null;

  function resetForm() {
    formData = {
      primary: '',
      target: '',
      phonetic: '',
      transliteration: '',
      categoryId: '',
      isLearned: false,
    };
    errorMessage = '';
    isSaving = false;
    translationSuggestions = [];
    selectedSuggestions = new Set();
    showSuggestions = false;
    showManualInput = false;
    translationSource = 'none';
  }

  $effect(() => {
    if (!open) {
      resetForm();
    } else if (open && mode === 'edit' && card) {
      // Load existing card data for edit mode
      formData.primary = card.primary;
      formData.target = card.target;
      formData.phonetic = card.phonetic || '';
      formData.transliteration = card.transliteration || '';
      formData.categoryId = card.category?.id || '';
      formData.isLearned = card.isLearned;
      errorMessage = '';
    }
  });

  // Fetch translations from API (only in add mode)
  async function fetchTranslations(text: string) {
    if (mode === 'edit') return; // Don't fetch translations in edit mode
    
    if (!text.trim() || !$selectedTargetLanguage || !$userSettings?.preferred_language) {
      return;
    }

    const apiUrl = resolveApiUrl('/api/translate');

    try {
      isLoadingTranslations = true;
      errorMessage = '';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text.trim(),
          sourceLanguage: $userSettings.preferred_language,
          targetLanguage: $selectedTargetLanguage.code,
        }),
      });

      if (!response.ok) {
        throw new Error(`Translation API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.translations && data.translations.length > 0) {
        translationSuggestions = data.translations;
        translationSource = data.source || 'none';
        showSuggestions = true;
        showManualInput = false;

        // Auto-select first suggestion
        if (translationSuggestions.length === 1) {
          selectedSuggestions = new Set([translationSuggestions[0]]);
          formData.target = translationSuggestions[0];
        }
      } else {
        // No translations available - show manual input
        translationSuggestions = [];
        showSuggestions = false;
        showManualInput = true;
      }
    } catch (error) {
      console.log('Translation API unavailable, using manual input:', error instanceof Error ? error.message : 'Unknown error');
      // On error, show manual input silently (don't show error to user)
      translationSuggestions = [];
      showSuggestions = false;
      showManualInput = true;
    } finally {
      isLoadingTranslations = false;
    }
  }

  // Debounced translation fetch when primary text changes (add mode only)
  $effect(() => {
    if (mode === 'edit') return; // Skip in edit mode
    
    const text = formData.primary;

    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Reset suggestions if text is empty
    if (!text.trim()) {
      translationSuggestions = [];
      showSuggestions = false;
      showManualInput = false;
      isDebouncing = false;
      return;
    }

    // Debounce translation fetch (800ms delay)
    isDebouncing = true;
    debounceTimer = setTimeout(() => {
      isDebouncing = false;
      fetchTranslations(text);
    }, 800) as unknown as number;
  });

  function toggleSuggestion(suggestion: string) {
    const newSet = new Set(selectedSuggestions);

    if (newSet.has(suggestion)) {
      newSet.delete(suggestion);
    } else {
      newSet.add(suggestion);
    }

    selectedSuggestions = newSet;

    // Update target field with selected suggestions (comma-separated)
    formData.target = Array.from(newSet).join(', ');
  }

  function useManualInput() {
    showManualInput = true;
    showSuggestions = false;
    formData.target = '';
    selectedSuggestions = new Set();
  }

  function handleClose() {
    if (isSaving) return;
    onClose?.();
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      handleClose();
    }
  }

  async function handleSubmit(event: Event) {
    event.preventDefault();

    if (!formData.primary.trim() || !formData.target.trim()) {
      errorMessage = $_('form.validation.required_fields');
      return;
    }

    const selectedCategory = formData.categoryId
      ? $filteredCategories.find((cat) => cat.id === formData.categoryId)
      : undefined;

    try {
      isSaving = true;
      errorMessage = '';

      if (mode === 'edit') {
        // Edit existing card
        await flashcardActions.updateFlashcard(cardId, {
          primary: formData.primary.trim(),
          target: formData.target.trim(),
          phonetic: formData.phonetic.trim(),
          transliteration: formData.transliteration.trim(),
          category: selectedCategory,
          isLearned: formData.isLearned
        });
      } else {
        // Add new card
        if (!$selectedTargetLanguage) {
          errorMessage = $_('flashcards.dialog.no_target_language');
          isSaving = false;
          return;
        }

        await flashcardActions.addFlashcard({
          primary: formData.primary.trim(),
          target: formData.target.trim(),
          phonetic: formData.phonetic.trim(),
          transliteration: formData.transliteration.trim(),
          category: selectedCategory,
          isLearned: false,
          targetLanguage: $selectedTargetLanguage,
          examples: [],
        });
      }

      resetForm();
      onClose?.();
    } catch (error) {
      console.error('Failed to save flashcard:', error);
      errorMessage =
        error instanceof Error ? error.message : $_('flashcards.dialog.save_failed');
    } finally {
      isSaving = false;
    }
  }
</script>

{#if open}
  <div
    class="fixed inset-0 z-[96] flex flex-col dialog-overlay"
    role="dialog"
    aria-modal="true"
    tabindex="0"
    onclick={handleBackdropClick}
    onkeydown={handleKeydown}
  >
    <div class="dialog-close dialog-close--fixed">
      <button
        type="button"
        class="btn btn-close"
        onclick={handleClose}
        aria-label={$_('buttons.cancel')}
      >
        <X class="h-5 w-5" />
      </button>
    </div>

    <div class="h-full w-full overflow-y-auto p-6 pt-20 pb-24">
      <div class="mx-auto max-w-2xl space-y-6">
        <header class="space-y-2">
          <p class="section-label text-muted-foreground">
            {#if mode === 'edit'}
              {$_('buttons.edit_cards')}
            {:else}
              {$_('buttons.add_cards')}
            {/if}
          </p>
          <h1 class="text-3xl font-semibold text-foreground">
            {#if mode === 'edit'}
              {$_('form.edit_flashcard_title')}
            {:else}
              {$_('form.add_flashcard_title')}
            {/if}
          </h1>
          <p class="text-sm text-muted-foreground">
            {#if mode === 'edit'}
              {$_('flashcards.dialog.edit_subtitle')}
            {:else}
              {$_('flashcards.dialog.add_subtitle')}
            {/if}
          </p>
        </header>

        {#if isLoading}
          <Card class="widget-card tone-header">
            <div class="flex flex-col items-center gap-4 py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              <p class="text-white">{$_('flashcards.dialog.loading_card')}</p>
            </div>
          </Card>
        {:else if mode === 'edit' && !card}
          <Card class="widget-card tone-header">
            <div class="text-center py-8">
              <p class="text-white">{$_('messages.card_not_found')}</p>
            </div>
          </Card>
        {:else}
          <Card class="widget-card tone-header">
        <form onsubmit={handleSubmit} class="form-layout">
          {#if errorMessage}
            <div class="rounded-2xl border border-destructive/40 bg-destructive/10 p-3 text-destructive">
              {errorMessage}
            </div>
          {/if}

          <div class="form-field">
            <label for="primary" class="form-label">
              {$_('form.primary_language_word')} ({primaryLanguageFlag})
            </label>
            <input
              id="primary"
              type="text"
              bind:value={formData.primary}
              placeholder={$_('form.primary_language_placeholder')}
              class="form-input"
            />
          </div>

          <div class="form-field">
            <label for="target" class="form-label">
              {$_('form.target_language_word')} ({targetLanguageFlag})
            </label>

            {#if isLoadingTranslations}
              <div class="flex items-center gap-3 rounded-lg border border-white/20 bg-white/10 px-4 py-3">
                <Loader2 class="h-5 w-5 animate-spin text-white/60" />
                <span class="text-sm text-white/60">{$_('flashcards.dialog.loading_translations')}</span>
              </div>
            {:else if showSuggestions && translationSuggestions.length > 0}
              <div class="space-y-2">
                <div class="flex items-center gap-2 text-xs text-white/60">
                  <Sparkles class="h-3 w-3" />
                  <span>
                    {$_('flashcards.dialog.suggestions_from', { values: { source: translationSourceLabel } })}
                  </span>
                  <button
                    type="button"
                    onclick={useManualInput}
                    class="ml-auto text-white/80 underline hover:text-white"
                  >
                    {$_('flashcards.dialog.enter_manually')}
                  </button>
                </div>

                <div class="space-y-2">
                  {#each translationSuggestions as suggestion}
                    {@const isSelected = selectedSuggestions.has(suggestion)}
                    <button
                      type="button"
                      onclick={() => toggleSuggestion(suggestion)}
                      class={`tap-target flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition ${
                        isSelected
                          ? 'border-primary/60 bg-primary/20'
                          : 'border-white/20 bg-white/10 hover:bg-white/20'
                      }`}
                    >
                      <div
                        class={`flex h-5 w-5 items-center justify-center rounded border-2 transition ${
                          isSelected
                            ? 'border-primary bg-primary'
                            : 'border-white/40'
                        }`}
                      >
                        {#if isSelected}
                          <Check class="h-3 w-3 text-white" />
                        {/if}
                      </div>
                      <span
                        class="flex-1 text-white"
                        dir={isTargetRTL ? 'rtl' : 'ltr'}
                      >
                        {suggestion}
                      </span>
                    </button>
                  {/each}
                </div>

                {#if selectedSuggestions.size === 0}
                  <p class="text-xs text-white/50">
                    {$_('flashcards.dialog.select_translations')}
                  </p>
                {/if}
              </div>
            {:else if showManualInput || formData.primary.trim()}
              <div class="space-y-2">
                {#if showManualInput}
                  <div class="flex items-center gap-2 text-xs text-white/60">
                    <span>{$_('flashcards.dialog.translation_unavailable')}</span>
                    {#if translationSuggestions.length > 0}
                      <button
                        type="button"
                        onclick={() => { showManualInput = false; showSuggestions = true; }}
                        class="ml-auto text-white/80 underline hover:text-white"
                      >
                        {$_('flashcards.dialog.show_suggestions')}
                      </button>
                    {/if}
                  </div>
                {/if}
                <input
                  id="target"
                  type="text"
                  bind:value={formData.target}
                  placeholder={$_('form.target_language_placeholder')}
                  dir={isTargetRTL ? 'rtl' : 'ltr'}
                  class="form-input"
                />
              </div>
            {:else}
              <input
                id="target"
                type="text"
                bind:value={formData.target}
                placeholder={$_('flashcards.dialog.primary_first_placeholder')}
                disabled
                dir={isTargetRTL ? 'rtl' : 'ltr'}
                class="form-input"
              />
            {/if}
          </div>

          {#if $userSettings?.show_ipa_phonetic}
            <div class="form-field">
              <label for="phonetic" class="form-label">
                {$_('form.phonetic')}
              </label>
              <p class="form-helper">{$_('form.phonetic_hint')}</p>
              <input
                id="phonetic"
                type="text"
                bind:value={formData.phonetic}
                placeholder={$_('form.phonetic_placeholder')}
                class="form-input"
              />
            </div>
          {:else}
            <div class="form-field">
              <label for="transliteration" class="form-label">
                {$_('form.transliteration_label')}
              </label>
              <p class="form-helper">{$_('form.transliteration_hint')}</p>
              <input
                id="transliteration"
                type="text"
                bind:value={formData.transliteration}
                placeholder={$_('form.transliteration_placeholder')}
                class="form-input"
              />
            </div>
          {/if}

          <div class="form-field">
            <label for="category" class="form-label">
              {$_('form.category')}
            </label>
            <select
              id="category"
              bind:value={formData.categoryId}
              class="form-select"
            >
                <option value="" class="text-muted-foreground">{$_('form.category_placeholder')}</option>
              {#each $filteredCategories as category}
                <option value={category.id}>{category.name}</option>
              {/each}
            </select>
          </div>

          {#if mode === 'edit'}
            <div class="flex items-center gap-3">
              <input
                id="learned"
                type="checkbox"
                bind:checked={formData.isLearned}
                class="form-checkbox"
              />
              <label for="learned" class="form-label">
                {$_('form.mark_as_learned')}
              </label>
            </div>
          {/if}

          <div class="form-actions">
            <button
              type="button"
              class="btn btn-secondary"
              onclick={handleClose}
              disabled={isSaving}
            >
              {$_('buttons.cancel')}
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              disabled={isSaving || isLoadingTranslations || isDebouncing}
            >
              {isSaving ? $_('buttons.saving') : mode === 'edit' ? $_('buttons.save_changes') : $_('buttons.save')}
            </button>
          </div>
        </form>
      </Card>
        {/if}
      </div>
    </div>
  </div>
{/if}
